import { calculate } from './calculator.js';

class PowerCalculator extends HTMLElement {
  constructor() {
    super();
    
    this.precision = 2;
    this.type = 'mw';

    this.ampRecommendationsComponent = null;

    this.calculatedValues = {
      voltage: 0,
      current: 0,
      power: 0,
      impedance: 0,
      price: 0,
      closedBack: false
    }

    this.ampSection = document.querySelector('.amp-recommendations-section');
  }

  connectedCallback() {
    this.initElements();
    this.bindHeadphoneEvents();
    this.bindCalculatorEvents();
    this.calculate();

    this.loadAllHeadphones();
  }

  initElements() {
    this.calculatorProduct = document.querySelector('#calculator-main-product');
    this.requireHeading = document.querySelector('.requirements__title');

    this.sensitivity = this.querySelector('#sensitivity');
    this.sensitivityUnit = this.querySelector('#sensitivity-unit');
    this.impedance = this.querySelector('#impedance');
    this.loudness = this.querySelector('#loudness');
    this.headphones = this.querySelector("#headphones");

    this.requiredVoltage = this.querySelector('[data-value="required-voltage"]');
    this.requiredCurrent = this.querySelector('[data-value="required-current"]');
    this.requiredPower = this.querySelector('[data-value="required-power"]');
    this.equivalent = this.querySelector('[data-value="equivalent"]');
    this.equivalentLabel = this.querySelector('[data-name="equivalent-label"]');

    this.sensitivityWatts = this.querySelector('[data-watts]').dataset.watts;
    this.sensitivityVolts = this.querySelector('[data-volts]').dataset.volts;

    this.inputs = [this.sensitivity, this.impedance, this.loudness];

    if (this.sensitivityUnit) {
      this.type = this.sensitivityUnit.value;
    }

    this.viewAmpsButton = this.querySelector('#viewAmps');

    this.dropdownButton = this.querySelector('#headphoneDropdownButton');
    this.dropdownPanel = this.querySelector('#headphoneDropdownPanel');
    this.selectedValue = this.querySelector('#headphoneSelectedValue');
    this.searchInput = this.querySelector('#headphoneSearchInput');
    this.optionsList = this.querySelector('#headphoneOptionsList');
    this.options = Array.from(this.optionsList.querySelectorAll('.panel-option'));
    this.activeIndex = -1;

    this.collectionHandle = this.dataset.collectionHandle;
  }

  bindHeadphoneEvents() {
    if (this.viewAmpsButton) {
      this.viewAmpsButton.addEventListener('click', () => {
        this.ampSection.scrollIntoView({ behavior: 'smooth' });
      });
    }

    this.dropdownButton.addEventListener("click", () => this.toggleDropdown());
    this.optionsList.addEventListener("click", this.handleOptionClick.bind(this));
    this.dropdownPanel.addEventListener("keydown", this.handleDropdownKeydown.bind(this));
    this.searchInput.addEventListener("input", this.handleSearchInput.bind(this));
    document.addEventListener("click", this.handleDocumentClick.bind(this));

  }

  handleOptionClick(event) {
    if (event.target && event.target.nodeName === "LI") {
      this.selectOption(event.target);
    }
  }

  handleDropdownKeydown(event) {
    const visibleOptions = this.options.filter((option) => option.style.display !== "none");

    if (visibleOptions.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.setActiveOption((this.activeIndex + 1) % visibleOptions.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.setActiveOption((this.activeIndex - 1 + visibleOptions.length) % visibleOptions.length);
        break;
      case "Enter":
        event.preventDefault();
        if (this.activeIndex > -1) {
          this.selectOption(visibleOptions[this.activeIndex]);
        }
        break;
      case "Escape":
        event.preventDefault();
        this.toggleDropdown(false);
        break;
    }
  }

  handleSearchInput() {
      const filter = this.searchInput.value.toLowerCase();
      this.options.forEach((option) => {
        const text = option.textContent.toLowerCase();
        option.style.display = text.includes(filter) ? "" : "none";
      });
      this.setActiveOption(-1);
  }

  handleDocumentClick(event) {
    if (!event.target.closest(".dropdown-container")) {
      this.toggleDropdown(false);
    }
  }

  toggleDropdown(show) {
    const isHidden = this.dropdownPanel.classList.contains("hidden");
    if (show === undefined) show = isHidden;

    this.dropdownPanel.classList.toggle("hidden", !show);
    this.dropdownButton.setAttribute("aria-expanded", show);

    if (show) {
      this.activeIndex = -1;
      this.removeActiveOption();
      this.resetSearchInput();
    }
  }

  resetSearchInput() {
    this.searchInput.value = '';
    this.searchInput.focus();
    this.optionsList.scrollTop = 0;
    this.options.forEach((option) => option.style.display = '');
  }

  selectOption(option) {
    if (!option) return;
    this.selectedValue.textContent = option.textContent;
    this.dropdownPanel.classList.add("hidden");
    this.dropdownButton.setAttribute("aria-expanded", "false");

    const handle = option.dataset.value;
    const name = option.textContent;
    const price = option.dataset.productPrice;
    const closedBack = option.dataset.closedBack !== undefined;

    this.getSelectedHeadphone(handle, name, price, closedBack);
  }

  removeActiveOption() {
    const currentActive = document.querySelector(".active-option");
    if (currentActive) {
      currentActive.classList.remove("active-option");
      currentActive.setAttribute("aria-selected", "false");
    }
    this.searchInput.setAttribute("aria-activedescendant", "");
  }

  setActiveOption(index) {
    this.removeActiveOption();
    const visibleOptions = this.options.filter((option) => option.style.display !== "none");
    if (index < 0 || index >= visibleOptions.length) {
      this.activeIndex = -1;
      return;
    }
    this.activeIndex = index;
    const activeOption = visibleOptions[this.activeIndex];
    activeOption.classList.add("active-option");
    activeOption.setAttribute("aria-selected", "true");
    this.searchInput.setAttribute("aria-activedescendant", activeOption.id);
    activeOption.scrollIntoView({ block: "nearest" });
  }

  bindCalculatorEvents() {
    this.sensitivityUnit.addEventListener('change', (event) => {
      this.type = event.target.value;

      if (this.type === "volts") {
        this.equivalentLabel.textContent = 'Equiv. dB/mW';
        this.sensitivity.value = parseFloat(this.sensitivityVolts);
      } else {
        this.equivalentLabel.textContent = 'Equiv. dB/1v';
        this.sensitivity.value = parseFloat(this.sensitivityWatts);
      }

      this.calculate();
    });

    this.inputs.forEach((input) => {
      input.addEventListener('input', () => {
        this.handleCustomInput();
        this.calculate();
        this.showViewAmpsButton();
      });
    });
  }

  async getSelectedHeadphone(handle, name, price, closedBack) {
    if (!handle) {
      this.handleCustomInput();
      this.calculate();
      return;
    }

    this.calculatedValues.price = parseFloat(price.replace(/,/g, ''));
    this.calculatedValues.closedBack = closedBack;
      
    try{
      const response = await fetch(`/products/${handle}?section_id=headphone-calculator`);

      if(!response.ok) {
        throw new Error('Failed to fetch product.');
      }
      const text = await response.text();

      this.getSectionsToRender().forEach((section) => {
        const updatedHTML = getSectionInnerHTML(text, section.selector);
        document.getElementById(section.id).innerHTML = updatedHTML;
      }); 

      this.requireHeading.textContent = `${name} Power Requirements`;

      this.initElements();
      this.bindCalculatorEvents();
      this.calculate();
    } catch(error) {
      console.warn('Error fetching selected headphone:', error);
    }

    this.showViewAmpsButton();
  }

  async loadAllHeadphones() {
    if (!this.collectionHandle) {
      console.warn('PowerCalculator: data-collection-handle is not set. Cannot fetch more products.');
      return;
    }

    try {
      const allProducts = await this.fetchAllProducts();
      this.sortAndPopulateDropdown(allProducts);
    } catch (error) {
      console.error('Could not fetch and populate produts: ', error);
    }
  }


  async fetchAllProducts() {
    let currentPage = 1;
    let allProducts = [];
    let allProductsLoaded = false;

    while (!allProductsLoaded) {
      const response = await fetch(`/collections/${this.collectionHandle}/products.json?page=${currentPage}`);

      if (!response.ok) {
        throw new Error(`HTTP Error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.products.length > 0) {
        allProducts = allProducts.concat(data.products);
        currentPage++;
      } else {
        allProductsLoaded = true;
      }
    }
    return allProducts;
  }


  sortAndPopulateDropdown(products) {
    products.sort((a, b) => a.title.localeCompare(b.title));

    this.optionsList.innerHTML = '';
    
    products.forEach(product => {

      if (product.tags.includes('no-power-specs')) {
         return;
      }

      const listItem = document.createElement('li');
      listItem.className = 'panel-option';
      listItem.dataset.value = product.handle;
      listItem.dataset.productId = product.id;
      listItem.dataset.title = product.title;
      listItem.dataset.productPrice = product.variants[0].price;
      listItem.setAttribute('role', 'option');
      listItem.setAttribute('aria-selected', 'false');

      if (product.tags.includes('closed-back')) {
        listItem.setAttribute('data-closed-back', '');
      }

      listItem.textContent = product.title;
      this.optionsList.appendChild(listItem);
    });

    this.options = Array.from(this.optionsList.querySelectorAll('.panel-option'));
  }

  getSectionsToRender() {
    return [
      {
        id: "calculator-main-product",
        selector: "#calculator-main-product"
      },
      {
        id: "product-specs",
        selector: "#product-specs"
      }
    ]
  }

  handleCustomInput() {
    this.calculatedValues.price = 0;
    this.requireHeading.textContent = 'Power Requirements';
    if (this.selectedValue) {
      this.selectedValue.textContent = 'Select a headphone';
    }
  }

  calculate() {
    const sensitivity = Number.parseFloat(this.sensitivity.value);
    const impedance =  Number.parseFloat(this.impedance.value);
    const loudness =  Number.parseFloat(this.loudness.value);

    this.sensitivity.value = sensitivity;
    this.impedance.value = impedance;

    const {
      requiredPower,
      requiredCurrent,
      requiredVoltage,
      equivalent,
    } = calculate(sensitivity, impedance, loudness, this.type);

    this.calculatedValues.voltage = requiredVoltage;
    this.calculatedValues.current = requiredCurrent;
    this.calculatedValues.power = requiredPower;
    this.calculatedValues.impedance = impedance;

    let displayValue = requiredPower * 1000;
    this.requiredPower.textContent = Number.isFinite(displayValue)
      ? displayValue.toFixed(this.precision)
      : "-";

    displayValue = requiredCurrent * 1000;
    this.requiredCurrent.textContent = Number.isFinite(displayValue)
      ? displayValue.toFixed(this.precision)
      : "-";

    this.requiredVoltage.textContent = Number.isFinite(requiredVoltage)
      ? requiredVoltage.toFixed(this.precision)
      : "-";
    this.equivalent.textContent = Number.isFinite(equivalent)
      ? equivalent.toFixed(this.precision)
      : "-";

    this.filterAmpRecommendations();

  }


  registerAmpRecommendations(component) {
    this.ampRecommendationsComponent = component;
    if (this.calculatedValues.power > 0) {
      const requirements = {
        requiredVoltage: this.calculatedValues.voltage,
        requiredCurrent: this.calculatedValues.current * 1000,
        requiredPower: this.calculatedValues.power * 1000,
        impedance: this.calculatedValues.impedance,
        price: this.calculatedValues.price || 0,
      };
      this.ampRecommendationsComponent.updateRecommendations(requirements);
    }
  }

  filterAmpRecommendations() {
    const requirements = {
      requiredVoltage: this.calculatedValues.voltage,
      requiredCurrent: this.calculatedValues.current * 1000, 
      requiredPower: this.calculatedValues.power * 1000,    
      impedance: this.calculatedValues.impedance,
      price: this.calculatedValues.price || 0,
      closedBack: this.calculatedValues.closedBack,
    };
    this.dispatchFilterEvent(requirements);
  }

  dispatchFilterEvent(detail) {
    const filterEvent = new CustomEvent('ampFilter', {
      detail: detail
    });
    document.dispatchEvent(filterEvent);
  }

  showViewAmpsButton() {
    if (this.viewAmpsButton) {
      this.viewAmpsButton.style.display = 'flex';
    }
  }

}


customElements.define('power-calculator', PowerCalculator);


function getSectionInnerHTML(html, selector) {
  return new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector(selector).innerHTML;
}














class AmpRecommendations extends HTMLElement {
  constructor() {
    super();

    this.allAmpItems = [];
    this.ampItemWidth = 0;
    this.ampsLoaded = false;
    this.currentRequirements = null;
    this.resizeObserver = null;
    this.collectionHandle = this.dataset.collectionHandle;
  }

  connectedCallback() {
    this.initElements();
    this.bindEvents();
    this.loadAllAmps();
    this.listenForCalculatorUpdates();

    const powerCalculator = document.querySelector('power-calculator');
    if (powerCalculator) {
      powerCalculator.registerAmpRecommendations(this);
    }
    this.setupResizeObserver();
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }


  initElements() {
    this.controls = this.querySelector('.amps-control');
    this.ampContainer = document.querySelector('.amp-container');
    this.itemContainer = document.querySelector('.item-container');
    this.ampList = this.querySelector('.amp-list');

    this.nextBtn = document.querySelector('.gallery-button--next');
    this.prevBtn = document.querySelector('.gallery-button--prev');

    this.ampCount = this.querySelector('.amps-count__total');
  } 

  storeAllAmpItems() {
    this.allAmpItems = Array.from(this.querySelectorAll('.amp-item'));
  }

  bindEvents() {
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.ampContainer.scrollBy({ left: this.getScrollAmount(), behavior: 'smooth' });
      });
    }
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.ampContainer.scrollBy({ left: -this.getScrollAmount(), behavior: 'smooth' });
      });
    }
    this.ampContainer.addEventListener('scroll', this.updateButtonStates.bind(this));
  }

  getScrollAmount() {
    const ampListItem = this.querySelector('.amp-item:not([style*="display: none"])');
    if (ampListItem) {
      const ampListStyle = window.getComputedStyle(this.ampList);
      const columnGap = parseFloat(ampListStyle.columnGap);
      return ampListItem.getBoundingClientRect().width + columnGap;
    }
    return 250;
  }

  updateButtonStates() {
    if (!this.ampContainer) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.ampContainer;
    this.prevBtn.disabled = scrollLeft < 1;
    this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
  }

  listenForCalculatorUpdates() {
    document.addEventListener('ampFilter', (event) => {
      this.updateRecommendations(event.detail);
    });
  }

  updateRecommendations(requirements) {
    this.currentRequirements = requirements;
    if (!this.ampsLoaded) return;

    this.classList.add('loading');

    setTimeout(() => {
 
      if (!this.allAmpItems.length) {
        this.storeAllAmpItems();
      }
  
      let compatibleCount = 0;
      let goodEnoughAmpFound = false;
      let recommendedAmpFound = false;
      let hasNoCompatibleMessage = this.querySelector('.no-compatible-amp');

      const compatibleItems = [];
  
      this.allAmpItems.forEach(ampItem => {
        const compatibilityResults = this.checkCompatibility(ampItem, requirements);
  
        ampItem.classList.remove('good-enough', 'recommended');
        
        if (compatibilityResults.isCompatible) {

          let priority = 3;
          
          ampItem.style.display = 'flex';
          ampItem.classList.remove('hidden');
          compatibleCount++;

          if (compatibilityResults.isRecommended && !recommendedAmpFound) {
            ampItem.classList.add('recommended');
            recommendedAmpFound = true;
            priority = 1;
          } else if (compatibilityResults.isGoodEnough && !goodEnoughAmpFound) {
            ampItem.classList.add('good-enough');
            goodEnoughAmpFound = true;
            priority = 2;
          }

          compatibleItems.push({
            element: ampItem,
            priority: priority
          });


        } else {
          ampItem.style.display = 'none';
          ampItem.classList.add('hidden');
        }
      });

      compatibleItems.sort((a, b) => a.priority - b.priority);
      
      compatibleItems.forEach(item => {
        this.ampList.appendChild(item.element);
      });
  
      if (compatibleCount === 0) {
        this.showNoCompatibleMessage(hasNoCompatibleMessage, true);
      } else {
        this.showNoCompatibleMessage(hasNoCompatibleMessage, false);
      }
  
      const summary = this.querySelector('.compatible-amps-summary');
      if (summary) {
        summary.setAttribute('data-rendered-count', compatibleCount);
        summary.textContent = `${compatibleCount} compatible amplifier${compatibleCount !== 1 ? 's' : ''} found`;
      }

      if (this.ampCount) {
        compatibleCount === 1 ? this.ampCount.textContent = `${compatibleCount} Amplifier` : this.ampCount.textContent = `${compatibleCount} Amplifiers`;
      }

      this.checkScrollable();
      this.classList.remove('loading');
      this.updateButtonStates();
      this.resetScrollPosition();
    }, 500);
  }

  showNoCompatibleMessage(messageElement, show) {
    let element = messageElement;
    if (show) {
      this.ampContainer.classList.add('no-results');
      if (!element) {
        element = document.createElement('li');
        element.className = 'no-compatible-amp';
        element.innerHTML = '<p>No compatible amplifiers found for these requirements</p>';
        this.ampList.appendChild(element);
      } else {
        element.style.display = 'flex';
      }
    } else {
      this.ampContainer.classList.remove('no-results');
      if (element) {
        element.style.display = 'none';
      }
    }
  }

  resetScrollPosition() {
    if (this.ampContainer) {
      this.ampContainer.scrollLeft = 0;
    }
  }


  checkScrollable() {
    if (!this.ampContainer || !this.controls) return;

    const isScrollable = this.ampContainer.scrollWidth > this.ampContainer.clientWidth;
    this.controls.style.display = isScrollable ? 'flex' : 'none';
  }

  setupResizeObserver() {
    if (!this.ampContainer) return;
    this.resizeObserver = new ResizeObserver(() => {
      this.checkScrollable();
      this.updateButtonStates();
    });
    this.resizeObserver.observe(this.ampContainer);
    this.resizeObserver.observe(this.itemContainer);
  }

  getImpedanceSuffix(impedance) {
    if (impedance <= 4) return 4;
    if (impedance <= 8) return 8;
    if (impedance <= 16) return 16;
    if (impedance <= 32) return 32;
    return 300;
  }

  checkCompatibility(ampItem, requirements) {
    let result = {
      isCompatible: false,
      isGoodEnough: false,
      isRecommended: false,
      technical: false
    }

    const impedanceSuffix = this.getImpedanceSuffix(requirements.impedance);

    let highImpedance = false;
    if (requirements.impedance >= 100){
      highImpedance = true;
    }

    const ampSpecs = JSON.parse(ampItem.dataset.specs);

    const ampVoltage = parseFloat(ampSpecs[`voltage_${impedanceSuffix}`]);
    const ampCurrent = parseFloat(ampSpecs[`current_${impedanceSuffix}`]);
    const ampPower = parseFloat(ampSpecs[`power_${impedanceSuffix}`]);
    const ampPrice = parseFloat(ampItem.getAttribute('data-product-price').replace(/,/g, ''));
    const ampPortable = ampItem.dataset.portable !== undefined;
    const ampHasTube = ampItem.dataset.tubeAmp !== undefined;
    const headphonePrice = requirements.price;
    const headphoneClosedBack = requirements.closedBack;

    if (ampVoltage >= requirements.requiredVoltage && ampCurrent >= requirements.requiredCurrent && ampPower >= requirements.requiredPower) {
      if (headphonePrice > 0) {
        if (ampPrice > (headphonePrice * 0.2)) {
          result.isGoodEnough = true;
        }
        if (ampPortable && headphoneClosedBack) {
          result.isRecommended = true;
          result.isGoodEnough = false;
        } else if (highImpedance) {
          if (ampHasTube && (ampPrice > (headphonePrice * 0.4))) {
            result.isRecommended = true;
            result.isGoodEnough = false;
          }
        } else if (ampPrice > (headphonePrice * 0.4)) {
          result.isRecommended = true;
          result.isGoodEnough = false;
        }
      }
      result.isCompatible = true;
    }
    return result;
  }




  async loadAllAmps() {
    if (!this.collectionHandle) {
      console.warn('Amp Recommendations: collection handle is not set.');
      return;
    }

    let currentPage = 1;
    let allProducts = [];
    let keepFetching = true;

    while (keepFetching){
      try {
        const response = await fetch(`/collections/${this.collectionHandle}?view=amps.json&page=${currentPage}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch data from collection: ${this.collectionHandle}`);
        }
  
        const data = await response.json();

        if (data.products && data.products.length > 0) {
          allProducts.push(...data.products);
          keepFetching = data.has_next_page === true;
        } else {
          keepFetching = false;
        }

        if (keepFetching) currentPage++;

      } catch (error) {
        console.error('Could not fetch amplifier products:', error);
        keepFetching = false;
      }
    }

    allProducts.sort((a, b) => {
      if (a.is_available !== b.is_available) {
        return b.is_available - a.is_available;
      }
      return a.price - b.price;
    });

    allProducts.forEach(productData => {
      const ampItem = this.createAmpItemElement(productData);
      this.ampList.appendChild(ampItem);
    });

    this.storeAllAmpItems();
    this.ampsLoaded = true;

    if (this.currentRequirements) {
      this.updateRecommendations(this.currentRequirements);
    }

  }

  createAmpItemElement(product) {
    const template = this.querySelector('#amp-item-template');
    const clone = template.content.cloneNode(true);
    const listItem = clone.querySelector('.amp-item');

    clone.querySelector('.card__link').textContent = product.title;
    clone.querySelector('.card__link').href = product.url;
    clone.querySelector('.card__image').src = product.featured_image.src;
    clone.querySelector('.card__image').alt = product.featured_image.alt;
    clone.querySelector('.card__price').innerHTML = product.price_html;

    listItem.dataset.productId = product.id;
    listItem.dataset.productHandle = product.handle;
    listItem.dataset.productPrice = product.price;
    listItem.dataset.tubeAmp = product.is_tube_amp;
    listItem.dataset.portable = product.is_portable;

    listItem.dataset.specs = JSON.stringify(product.specs);

    listItem.dataset.availability = product.availability;
    
    return listItem;
  }

}

customElements.define('amp-recommendations', AmpRecommendations);